/* eslint-disable default-case */
import React, { useState, useEffect } from 'react'
import { CheckCircleOutlined, CloseSquareOutlined, RetweetOutlined, StarFilled, TeamOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGenderless, faVenus, faShield, faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { Col, Row, Select, Popover, Tag, Table, Modal } from 'antd'
import Button from 'antd-button-color'
import _ from 'lodash'

// import './css/tournament-pools.css'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import useWindowDimensions from '../../../../hooks/getWindowDimensions'

import { populatePools, generateAvailablePoolSizes, squadronHelper } from '../../../../shared/pools'
import { poolColumns } from '../../../../shared/tables'

import './css/tournament-pools.css'

const displayDemographic = (demographic) => {
  let icon = <FontAwesomeIcon icon={faCircleQuestion} />
  let content = (
    <div>Demographic not entered</div>
  )
  switch (demographic) {
    case 'open': {
      icon = <FontAwesomeIcon color='green' icon={faGenderless} />
      content = (
        <div>Open</div>
      )
      break
    }
    case 'ladies': {
      icon = <FontAwesomeIcon color='purple' icon={faVenus} />
      content = (
        <div>Ladies</div>
      )
      break
    }
    case 'cadet': {
      icon = <FontAwesomeIcon color='blue' icon={faShield} />
      content = (
        <div>Cadet</div>
      )
    }
  }
  return (
    <Popover content={content}>
      {icon}
    </Popover>
  )
}

const TournamentPools = (props) => {
  const { height } = useWindowDimensions()
  const [pools, setPools] = useState([])
  const [selectedPoolConfig, setSelectedPoolConfig] = useState('')
  const [poolsPopulated, setPoolsPopulated] = useState([])
  const [participantList, setParticipantList] = useState([])
  const [noShowParticipantList, setNoShowParticipantList] = useState([])
  const [isPoolsVisible, setIsPoolsVisible] = useState(true)
  const [isListVisible, setIsListsVisible] = useState(false)
  const [isLatecomerVisible, setIsLatecomerVisible] = useState(false)
  const [isAbsentVisible, setIsAbsentVisible] = useState(false)
  const [allParticipants, setAllParticipants] = useState([])

  const [tournament, setTournament] = useState({})
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const newTournament = props.tournament
    // clear next step
    const nextStep = props.step + 1
    let stepName = props.tournament.schedule[nextStep]
    if (stepName === 'bestof') {
      stepName = 'table'
    }
    delete newTournament[`${stepName}_${nextStep}`]

    setTournament(newTournament)
    const participants = newTournament?.participants?.filter(f => f.presence)
    const poolList = generateAvailablePoolSizes(participants?.length)
    setPools(poolList)

    if (_.isEmpty(newTournament?.[`pools_${props.step}`])) {
      const preferredPoolConfig = poolList.find(p => p.preferred)
      const poolLayout = populatePools(preferredPoolConfig, participants)
      setPoolsPopulated(poolLayout)
      setSelectedPoolConfig(preferredPoolConfig?.id)
    } else {
      setPoolsPopulated(newTournament?.[`pools_${props.step}`])
      const preferredPoolConfig = poolList.find(p => p.numPools === newTournament?.[`pools_${props.step}`].length)
      setSelectedPoolConfig(preferredPoolConfig?.id)
    }
    setAllParticipants(newTournament?.participants)
    setIsLoaded(true)
    // eslint-disable-next-line
  }, [props.tournament])

  const presenceColumns = [
    {
      title: 'presence',
      dataIndex: 'presence',
      sorter: (a, b) => {
        if (a.presence < b.presence) return -1
        if (a.presence > b.presence) return 1
        return 0
      },
      display: true,
      align: 'right',
      width: '5em',
      render: (value, record) => {
        let icon = (<CloseSquareOutlined />)
        let color = 'participant-absent'
        if (value) {
          icon = (<CheckCircleOutlined />)
          color = 'participant-present'
        }
        return (<Button type='primary' shape='circle' icon={icon} onClick={() => { updatePresence(value, record.uuid) }} className={color} />)
      }
    },
    {
      title: 'last name',
      dataIndex: 'last_name',
      sorter: (a, b) => {
        if (a.last_name < b.last_name) return -1
        if (a.last_name > b.last_name) return 1
        return 0
      },
      display: true
    },
    {
      title: 'first name',
      dataIndex: 'first_name',
      sorter: (a, b) => {
        if (a.first_name < b.first_name) return -1
        if (a.first_name > b.first_name) return 1
        return 0
      },
      display: true
    },
    {
      title: 'age',
      dataIndex: 'age',
      sorter: (a, b) => a.age - b.age,
      display: true
    },
    {
      title: 'demographic',
      dataIndex: 'demographic',
      sorter: (a, b) => {
        if (a.demographic < b.demographic) return -1
        if (a.demographic > b.demographic) return 1
        return 0
      },
      display: true,
      render: (value) => {
        let color = 'default'
        switch (value) {
          case 'open': {
            color = 'green'
            break
          }
          case 'ladies': {
            color = 'purple'
            break
          }
          case 'cadet': {
            color = 'blue'
          }
        }
        return (
          <Tag color={color} key={value}>
            {value?.toUpperCase()}
          </Tag>
        )
      }
    },
    {
      title: 'club',
      dataIndex: 'club',
      sorter: (a, b) => {
        if (a.club < b.club) return -1
        if (a.club > b.club) return 1
        return 0
      },
      display: true
    },
    {
      title: 'rating',
      dataIndex: 'rating',
      sorter: (a, b) => {
        if (a.rating < b.rating) return -1
        if (a.rating > b.rating) return 1
        return 0
      },
      display: true
    },
    {
      title: 'squadron',
      dataIndex: 'pool',
      sorter: (a, b) => {
        if (a.pool < b.pool) return -1
        if (a.pool > b.pool) return 1
        return 0
      },
      display: true,
      render: (value) => {
        return squadronHelper.getColor(value - 1)
      }
    }
  ]

  const saveTournamentData = () => {
    const tournamentData = Object.assign({}, tournament)
    tournamentData[`pools_${props.step}`] = poolsPopulated
    props.onTournamentUpdate(tournamentData)
  }

  useEffect(() => {
    const list = []

    for (const pool of poolsPopulated) {
      pool.participants.map(p => {
        const participant = Object.assign({}, p, { pool: pool.index + 1 }, { key: p.uuid })
        list.push(participant)
        return participant
      })
    }

    list.sort((a, b) => {
      if (a.last_name < b.last_name) return -1
      if (a.last_name > b.last_name) return 1
      return 0
    })

    setParticipantList(list)
    if (isLoaded) {
      saveTournamentData()
    }
    // eslint-disable-next-line
  }, [poolsPopulated])

  useEffect(() => {
    const participants = allParticipants?.filter(f => !f.presence)
    setNoShowParticipantList(participants)
    // eslint-disable-next-line
  }, [allParticipants])

  const handlePoolSelected = (value) => {
    const participants = allParticipants.filter(f => f.presence)
    const poolConfig = pools.find(p => p.id === value)
    const poolLayout = populatePools(poolConfig, participants)
    _.times(poolConfig.numPools, (i) => { pools.push({ index: i, participants: {} }) })
    setSelectedPoolConfig(value)
    setPoolsPopulated(poolLayout)
  }

  const handleDrop = (result) => {
    const { source, destination } = result
    if (!destination) {
      return
    }
    const sInd = +source.droppableId
    const dInd = +destination.droppableId
    const sourcePool = poolsPopulated[sInd]
    const destPool = poolsPopulated[dInd]

    const [removed] = sourcePool.participants.splice(source.index, 1)

    destPool.participants.splice(destination.index, 0, removed)

    sourcePool.participants = sourcePool.participants.map((p, index) => { return Object.assign({}, p, { seed: index }) })
    destPool.participants = destPool.participants.map((p, index) => { return Object.assign({}, p, { seed: index }) })

    setPoolsPopulated(prevState => {
      const newState = Array.from(prevState)
      newState[sInd] = sourcePool
      newState[dInd] = destPool
      return newState
    })
  }

  const handleFencerList = () => {
    setIsListsVisible(prevState => !prevState)
    setIsPoolsVisible(prevState => !prevState)
  }

  const dynamicSizing = {
    y: height - 450
  }

  const handleAbsent = () => {
    setIsAbsentVisible(true)
  }

  const handleLatecomer = () => {
    setIsLatecomerVisible(true)
  }

  const handleCloseModal = () => {
    setIsAbsentVisible(false)
    setIsLatecomerVisible(false)
  }

  const updatePresence = (value, uuid) => {
    const list = Array.from(participantList)
    const listAll = Array.from(allParticipants)
    const populatedPools = Array.from(poolsPopulated)

    if (!value) {
      const newParticipantIndex = noShowParticipantList.findIndex(n => n.uuid === uuid)
      const newParticipant = Object.assign({}, noShowParticipantList[newParticipantIndex], { presence: true })
      list.push(newParticipant)
      const poolList = generateAvailablePoolSizes(list.length)
      const preferredPoolConfig = poolList.find(p => p.preferred)

      let index = populatedPools.findIndex(p => p.participants.length <= preferredPoolConfig.poolSize)
      if (index < 0) index = 0
      populatedPools[index].participants.push(newParticipant)
      const listAllIndex = listAll.findIndex(n => n.uuid === uuid)
      listAll[listAllIndex].presence = true

      setPools(poolList)
      setSelectedPoolConfig(preferredPoolConfig.id)
      setPoolsPopulated(populatedPools)
      setAllParticipants(listAll)
    } else {
      const participantIndex = list.findIndex(n => n.uuid === uuid)
      const participantPoolIndex = populatedPools[list[participantIndex].pool - 1].participants.findIndex(p => p.uuid === uuid)
      populatedPools[list[participantIndex].pool - 1].participants.splice(participantPoolIndex, 1)
      list.splice(participantIndex, 1)

      const poolList = generateAvailablePoolSizes(list.length)
      const preferredPoolConfig = poolList.find(p => p.preferred)
      const listAllIndex = listAll.findIndex(n => n.uuid === uuid)
      listAll[listAllIndex].presence = false

      setPools(poolList)
      setSelectedPoolConfig(preferredPoolConfig.id)
      setPoolsPopulated(populatedPools)
      setAllParticipants(listAll)
    }
  }

  const handleReseed = () => {
    const participants = allParticipants.filter(f => f.presence)
    const preferredPoolConfig = pools.find(p => p.id === selectedPoolConfig)
    const poolLayout = populatePools(preferredPoolConfig, participants)

    setPoolsPopulated(poolLayout)
  }

  return (
    <div className={props.hidden ? 'hidden' : ''}>
      <div className='tournament-menu tournament-pools'>
        <Row gutter={[8]}>
          <Col flex='none'>
            Number of pools
            <Select style={{ width: '6.5em', paddingLeft: 10 }} value={selectedPoolConfig} onChange={handlePoolSelected}>
              {pools.map(p =>
                <Select.Option key={`np_${p.id}`} value={p.id}>{p.numPools}{p.preferred ? (<StarFilled style={{ color: '#fcd703' }} />) : ''}</Select.Option>
              )}
            </Select>
          </Col>
          <Col flex='none'>
            Fencers / pool
            <Select style={{ width: '8.5em', paddingLeft: 10 }} value={selectedPoolConfig} onChange={handlePoolSelected}>
              {pools.map((p, index) =>
                <Select.Option key={`ps_${index}`} value={p.id}>{p.text}{p.preferred ? (<StarFilled style={{ color: '#fcd703' }} />) : ''}</Select.Option>
              )}
            </Select>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleFencerList} type='info'>
              <TeamOutlined />
              {`${isPoolsVisible ? 'Fencer List' : 'Pools Setup'}`}
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleLatecomer} type='info'>
              <UserAddOutlined />
              Latecomer
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleAbsent} type='info'>
              <UserDeleteOutlined />
              Absent
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleReseed} type='info'>
              <RetweetOutlined />
              Reseed Pools
            </Button>
          </Col>
        </Row>
      </div>
      <Row style={{ marginTop: '1em' }} className={`tournament-pools pool-list ${!isPoolsVisible ? 'd-none' : ''}`}>
        <DragDropContext onDragEnd={handleDrop}>
          {poolsPopulated.map(pool =>
            <Droppable key={pool.index} droppableId={`${pool.index}`}>
              {(provided) => (
                <Col
                  span={3}
                  className='pool-layout'
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  <div className={`pool-title pool_${squadronHelper.getColor(pool?.index)}`}>{squadronHelper.getColor(pool?.index)} Squadron</div>
                  {pool.participants.map((participant, index) => (
                    <Draggable key={participant.uuid} draggableId={participant.uuid} index={index}>
                      {(provided) => (
                        <Col
                          className='pool-participant'
                          ref={provided.innerRef}
                          {...provided.dragHandleProps}
                          {...provided.draggableProps}
                        >
                          <Row gutter={6}>
                            <Col span={17}>
                              <Popover content={(
                                <>
                                  <Row>
                                    <Col span={12}>Age</Col>
                                    <Col span={12}>Club</Col>
                                  </Row>
                                  <Row>
                                    <Col span={12}>{participant.age}</Col>
                                    <Col span={12}>{participant.club}</Col>
                                  </Row>
                                </>
                              )}
                              >
                                {participant.last_name}, {_.first(participant.first_name)}
                              </Popover>
                            </Col>
                            <Col span={4}>{displayDemographic(participant.demographic)}</Col>
                            <Col span={3}>{participant.rating}</Col>
                          </Row>
                        </Col>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Col>
              )}
            </Droppable>
          )}
        </DragDropContext>
      </Row>
      <Row className={`${!isListVisible ? 'd-none' : ''}`}>
        <Table pagination={false} scroll={dynamicSizing} size='small' dataSource={participantList} columns={poolColumns} />
      </Row>
      <Modal
        title='Latecomer' visible={isLatecomerVisible} onCancel={handleCloseModal} maskClosable={false} destroyOnClose width={800} footer={[
          <Button key='done' onClick={handleCloseModal}>
            Done
          </Button>
        ]}
      >
        <Table pagination={false} scroll={dynamicSizing} size='small' dataSource={noShowParticipantList} columns={presenceColumns} className='checkin-participants' />
      </Modal>
      <Modal
        title='Absent' visible={isAbsentVisible} onCancel={handleCloseModal} maskClosable={false} destroyOnClose width={800} footer={[
          <Button key='done' onClick={handleCloseModal}>
            Done
          </Button>
        ]}
      >
        <Table pagination={false} scroll={dynamicSizing} size='small' dataSource={participantList} columns={presenceColumns} className='checkin-participants' />
      </Modal>
    </div>
  )
}

export default TournamentPools
